import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Stethoscope, Syringe, FlaskConical, HeartPulse, CalendarClock, ChevronRight } from "lucide-react";

export const HC_TILES = [
    { kind: "nursing", icon: Syringe, label: "Nursing at Home", short: "Nursing at Home", desc: "Dressing, injections, vitals & more" },
    { kind: "doctor", icon: Stethoscope, label: "Doctor Visit", short: "Doctor Visit", desc: "Consultation at your home" },
    { kind: "lab", icon: FlaskConical, label: "Lab Tests at Home", short: "Lab Tests", desc: "Blood tests and other diagnostics" },
    { kind: "ecg", icon: HeartPulse, label: "ECG at Home", short: "ECG at Home", desc: "ECG recorded at home with report" },
];

export const HC_CARD = "rounded-2xl bg-white shadow-[0_10px_28px_-14px_rgba(16,42,67,0.18)]";

function UpcomingRow({ b }) {
    const nav = useNavigate();
    return (
        <button data-testid={`hc-upcoming-${b.ref}`} onClick={() => nav(`/care/healthcare/bookings/${b.id}`)} className="mt-3 flex w-full items-center gap-3 rounded-2xl bg-white p-3 text-left transition-transform active:scale-[0.98]">
            <CalendarClock size={18} className="shrink-0 text-teal" />
            <div className="min-w-0 flex-1 text-xs">
                <p className="font-bold text-navy">{b.kind_label} · {b.patient_name}</p>
                <p className="text-navy/55">{b.date} · {b.slot}{b.provider ? ` · ${b.provider.name}` : ""} · {b.status}</p>
                {b.quote?.prep?.[0] && <p className="text-amber-700">Prep: {b.quote.prep[0]}</p>}
            </div>
            <ChevronRight size={16} className="text-navy/30" />
        </button>
    );
}

export function HealthcareCard() {
    const nav = useNavigate();
    const [cat, setCat] = useState(null);
    const [bookings, setBookings] = useState([]);
    useEffect(() => {
        api.get("/care/healthcare/catalog").then((r) => setCat(r.data)).catch(() => setCat({ kinds: {} }));
        api.get("/care/healthcare/bookings").then((r) => setBookings(r.data)).catch(() => {});
    }, []);
    const upcoming = bookings.filter((b) => !["Cancelled", "Visit Completed", "Report Available", "Payment Pending"].includes(b.status)).slice(0, 2);
    return (
        <div className="mt-5 px-5">
            <div data-testid="healthcare-card" className="rounded-3xl bg-aqua-light/70 p-5 ring-1 ring-teal/10">
                <div className="flex items-center gap-2">
                    <span className="rounded-full bg-teal px-2 py-0.5 text-[10px] font-extrabold tracking-wide text-white">NEW</span>
                    <h2 className="font-heading text-xl font-extrabold text-navy">Healthcare at Home</h2>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-navy/60">Nursing visits, doctor visits and diagnostic tests, coordinated by HOMIIE Care.</p>
                <div className="mt-4 grid grid-cols-4 gap-2">
                    {HC_TILES.map((t, i) => {
                        const on = cat?.kinds?.[t.kind]?.available;
                        return (
                            <button key={t.kind} data-testid={`hc-tile-${t.kind}`} onClick={() => nav(`/care/healthcare/${t.kind}`)} style={{ animationDelay: `${i * 60}ms` }}
                                className="flex animate-in fade-in slide-in-from-bottom-2 flex-col items-center rounded-2xl bg-white px-1 py-3 text-center shadow-sm transition-transform duration-200 hover:-translate-y-0.5 active:scale-95">
                                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-aqua-light text-teal"><t.icon size={20} /></span>
                                <span className="mt-1.5 text-[11px] font-bold leading-tight text-navy">{t.short}</span>
                                <span data-testid={`hc-tile-status-${t.kind}`} className={`mt-1 text-[9px] font-bold ${on ? "text-emerald-600" : "text-navy/40"}`}>{cat === null ? "…" : on ? "Available" : "Coming soon"}</span>
                            </button>
                        );
                    })}
                </div>
                <button data-testid="hc-explore-btn" onClick={() => nav("/care/healthcare")} className="mt-4 flex w-full items-center justify-center gap-1 rounded-2xl bg-teal py-3.5 text-sm font-bold text-white transition-transform active:scale-[0.98]">Explore Healthcare Services <ChevronRight size={16} /></button>
                {upcoming.map((b) => <UpcomingRow key={b.id} b={b} />)}
            </div>
        </div>
    );
}
